/**
 * Modal viewport-sync — keep a modal's height in sync with the *visible* viewport.
 *
 * Extracted from the model-finder modal's bespoke
 * `getVisibleViewportHeight` / `applyHeaderOffset` / `bindViewportSync`
 * (`model-finder-page-modal.ts:88–160`) into a generic, opt-in behaviour module.
 * The MF-specific custom-prop names (`--mf-modal-*`) are renamed to the
 * namespaced `--datte-modal-*` so any modal can read them.
 *
 * Why this exists: on mobile the on-screen keyboard shrinks the *visual* viewport
 * but not `window.innerHeight`; using `visualViewport.height` gives the real
 * drawable area so the sheet (and its scroll container) never hides content
 * behind the keyboard. Only modals that opt in via `data-datte-viewport-sync`
 * (today only #1) need it — the base CSS `100dvh` is the no-JS fallback.
 *
 * Writes two custom properties on the modal element:
 *   --datte-modal-content-height  — the height the content box should take.
 *   --datte-modal-header-offset   — the fixed site-header height (mobile sheet
 *                                   positioning); only written when > 0.
 *
 * @see docs/architecture/modals/target-architecture.md §3.2.1
 */

import type { Unbind } from '../utils/unbind';
import { getSiteHeaderHeight } from '../utils/header-offset';

/** Custom-property names the modal CSS reads. Namespaced (was `--mf-modal-*`). */
export const CONTENT_HEIGHT_VAR = '--datte-modal-content-height';
export const HEADER_OFFSET_VAR = '--datte-modal-header-offset';
/** SCSS single-source-of-truth for the desktop height cap, read back here. */
const DESKTOP_CAP_VAR = '--datte-modal-max-height';
/** Fallback when the SCSS cap var is absent (e.g. stylesheet not yet applied). */
const DESKTOP_CAP_FALLBACK = 900;
/** Phone breakpoint — must match the SCSS `max-width: 575px` mobile sheet. */
const MOBILE_MAX_WIDTH = 575;

/**
 * The visible viewport height in CSS px. `visualViewport.height` shrinks when
 * the on-screen keyboard appears (unlike `window.innerHeight`).
 */
function getVisibleViewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

/**
 * Compute the content height for a given visible viewport height.
 *
 * Mobile: full-page takeover, so the content gets the FULL visible viewport
 * height (floored at 280 so it never collapses). Desktop: the visible height
 * minus a 32px margin, capped at `desktopCap`.
 *
 * Pure — the prime unit-test target.
 */
export function computeContentHeight(
  visibleVh: number,
  isMobile: boolean,
  desktopCap: number
): number {
  return isMobile ? Math.max(280, visibleVh) : Math.min(visibleVh - 32, desktopCap);
}

/** Read a px-valued CSS custom property, or `fallback` when unset/unparseable. */
function readPxVar(el: HTMLElement, name: string, fallback: number): number {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Write the header-offset + content-height custom props onto the modal element. */
function applyViewport(modal: HTMLElement): void {
  const headerHeight = getSiteHeaderHeight();
  if (headerHeight > 0) {
    modal.style.setProperty(HEADER_OFFSET_VAR, `${headerHeight}px`);
  }

  const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
  const desktopCap = readPxVar(modal, DESKTOP_CAP_VAR, DESKTOP_CAP_FALLBACK);
  const contentHeight = computeContentHeight(getVisibleViewportHeight(), isMobile, desktopCap);
  modal.style.setProperty(CONTENT_HEIGHT_VAR, `${contentHeight}px`);
}

/**
 * Apply the viewport custom props once and keep them in sync with real-time
 * viewport changes (virtual-keyboard show/hide, orientation, mobile browser
 * chrome). Returns an unbind that removes every listener — call it on modal
 * close so listeners never accumulate across open/close cycles.
 */
export function bindModalViewportSync(modal: HTMLElement): Unbind {
  const update = () => applyViewport(modal);
  update();

  const vv = window.visualViewport;
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  if (vv) {
    vv.addEventListener('resize', update);
    // iOS scrolls the visual viewport instead of resizing it when the keyboard opens.
    vv.addEventListener('scroll', update);
  }

  return () => {
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
    if (vv) {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    }
  };
}
